import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useSafeInteractionParams } from '@/common/hooks/useSafeInteractionParams';
import { interactionTypeRootPathName } from '@/helpers';
import {
  missingFieldId,
  missingFieldLinkCardButtonId,
} from '@/state/navigation';
import { TrackCoverSheetTaggingEvent } from '$analytics/useTrackAnalytics/domains/coverSheet/events';
import { useTrackCoverSheetAnalytics } from '$analytics/useTrackAnalytics/domains/coverSheet/useTrackCoverSheetAnalytics';

export const useNavigateToMissingField = ({
  label,
  cardButtonId,
  fieldName,
  isMobile = false,
  formPath = 'cover-sheet',
}: {
  label?: string | null;
  cardButtonId?: string | null;
  fieldName?: string | null;
  isMobile?: boolean;
  formPath?: string;
}) => {
  const router = useRouter();
  const trackEvent = useTrackCoverSheetAnalytics();
  const { interactionId, interactionType } = useSafeInteractionParams();
  const [missingFieldCardButtonId, setMissingFieldCardButtonId] = useAtom(
    missingFieldLinkCardButtonId,
  );
  const [missingRequiredFieldId, setMissingRequiredFieldId] =
    useAtom(missingFieldId);

  const handleNavigateToField = async () => {
    trackEvent(TrackCoverSheetTaggingEvent.ClickNavigateToMissingField, {
      field: label,
    });

    if (!router.pathname.includes(`/${formPath}`)) {
      if (cardButtonId) {
        setMissingFieldCardButtonId(cardButtonId);
      }
      if (fieldName) {
        setMissingRequiredFieldId(fieldName);
      }

      const rootPath = interactionTypeRootPathName(interactionType);

      await router.push(`/${rootPath}/${interactionId}/${formPath}`);
    }

    // check first for parameter passed, if exists, navigate to that field
    if (cardButtonId) {
      document.getElementById(cardButtonId)?.click();
    }

    if (fieldName) {
      const fieldEl = document.getElementsByName(fieldName)[0];

      if (isMobile && fieldEl) {
        const { position, top } = fieldEl.style;
        fieldEl.style.position = 'relative';
        fieldEl.style.top = '-30px';
        fieldEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        fieldEl.style.top = top;
        fieldEl.style.position = position;
      } else {
        fieldEl?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      setTimeout(() => {
        document
          .getElementsByName(fieldName)[0]
          ?.focus({ preventScroll: true });
      }, 500);

      return;
    }

    // if no parameter, check jotai value and navigate to that field
    if (missingFieldCardButtonId) {
      document.getElementById(missingFieldCardButtonId)?.click();
      setMissingFieldCardButtonId(null);
    }

    // try to find the element 5 times with a timeout to allow for rendering time
    let retryCount = 0;
    function elementRetry() {
      setTimeout(() => {
        retryCount += 1;
        if (missingRequiredFieldId) {
          const missingField = document.getElementsByName(
            missingRequiredFieldId,
          )[0];
          if (missingField) {
            missingField?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            missingField?.focus({ preventScroll: true });
            setMissingRequiredFieldId(null);
            return;
          }
          if (retryCount === 5) {
            // biome-ignore lint/suspicious/noConsole: Used for debugging
            console.warn(
              `Navigate to field ${missingRequiredFieldId} failed, unable to find field in the DOM.`,
            );
            return;
          }
          elementRetry();
        }
      }, 250);
    }

    elementRetry();
  };

  return {
    handleNavigateToField,
  };
};
